import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import CircleExtras from "@/components/circle/CircleExtras";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { shortDate, initials } from "@/lib/format";
import { Users, Loader2, Plus, LogIn, Send, Mail, Copy, Link2, Trash2, UserPlus, Pin, Activity } from "lucide-react";

const LANDING = "https://my-within.com";

const HEALTH = {
  green: { dot: "bg-emerald-500", labelKey: "cir.health_active" },
  yellow: { dot: "bg-amber-500", labelKey: "cir.health_quiet" },
  red: { dot: "bg-rose-500", labelKey: "cir.health_still" },
};
const KIND = {
  local: { label: "LOCAL", cls: "bg-emerald-100 text-emerald-700" },
  individual: { labelKey: "cir.kind_individual", cls: "bg-amber-100 text-amber-700" },
  inner: { label: "Inner Circle", cls: "bg-teal-100 text-teal-700" },
};

export default function Circles() {
  const t = useT();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isCoach = user?.role === "coach";
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [newCircle, setNewCircle] = useState({ name: "", description: "" });
  const [newKind, setNewKind] = useState("local");
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [membersOpen, setMembersOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const scrollRef = useRef(null);
  const [pinnedReload, setPinnedReload] = useState(0);

  const isOwner = active && isCoach && active.coach_id === user?.id;

  const load = async () => {
    try {
      const { data } = await api.get("/circles");
      const rows = Array.isArray(data) ? data : data.rows || [];
      setCircles(rows);
      return rows;
    } catch (err) { toast.error(apiError(err, t("cir.load_error"))); return []; }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadMembers = async (circleId) => {
    try {
      const m = await api.get(`/circles/${circleId}/members`);
      setMembers(m.data?.members || []);
    } catch (err) { toast.error(apiError(err)); }
  };
  const loadMessages = async (circleId) => {
    try {
      const { data } = await api.get(`/circles/${circleId}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch { /* keep existing on transient error */ }
  };

  const open = async (c) => {
    setActive(c); setMembers([]); setMessages([]);
    await Promise.all([loadMembers(c.id), loadMessages(c.id)]);
  };

  // Live-poll the active circle's chat every 6s, matching the mobile app.
  useEffect(() => {
    if (!active?.id) return;
    const id = setInterval(() => loadMessages(active.id), 6000);
    return () => clearInterval(id);
  }, [active?.id]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  // Auto-open a circle passed via ?c= (e.g. from the coach hub).
  useEffect(() => {
    const cid = searchParams.get("c");
    if (!cid || active || circles.length === 0) return;
    const c = circles.find((x) => x.id === cid);
    if (c) open(c);
  }, [circles, searchParams, active]);

  const send = async () => {
    const b = msg.trim();
    if (!b || !active) return;
    setMsg("");
    setMessages((m) => [...m, { id: `local-${Date.now()}`, from_user_id: user.id, from_name: user.name, body: b, created_at: new Date().toISOString() }]);
    try { await api.post("/messages", { circle_id: active.id, body: b }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data: created } = await api.post("/circles", {
        name: newCircle.name.trim(),
        description: newCircle.description.trim(),
        circle_kind: newKind,
      });
      toast.success(t("cir.created"));
      setNewCircle({ name: "", description: "" });
      setNewKind("local");
      setCreateOpen(false);
      await load();
      if (created?.id) open(created); // drop coach straight into the new circle
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const join = async () => {
    setBusy(true);
    try { await api.post("/circles/join", { invitation_code: joinCode.trim() }); toast.success(t("cir.joined")); setJoinCode(""); load(); }
    catch (err) { toast.error(apiError(err, t("cir.join_failed"))); } finally { setBusy(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim() || !active) return;
    setBusy(true);
    try {
      await api.post(`/circles/${active.id}/invite`, { email: inviteEmail.trim().toLowerCase(), note: "" });
      toast.success(t("cir.invite_sent"));
      setInviteEmail("");
      await Promise.all([loadMembers(active.id), load()]);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    const m = removeTarget;
    if (!m || !active) return;
    try {
      await api.delete(`/circles/${active.id}/members/${m.id}`);
      toast.success(t("cir.member_removed"));
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      await load();
    } catch (err) { toast.error(apiError(err, t("cir.remove_failed"))); }
    finally { setRemoveTarget(null); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(t("cir.code_copied"));
  };
  const shareLink = () => {
    const url = `${LANDING}/invite/circle/${active.invitation_code}`;
    navigator.clipboard?.writeText(url);
    toast.success(t("cir.link_copied"));
  };
  const togglePin = async (mid) => {
    try {
      await api.post(`/circles/${active.id}/messages/${mid}/pin`);
      setPinnedReload((n) => n + 1);
      toast.success(t("cir.pin_updated"));
    } catch (err) { toast.error(apiError(err)); }
  };

  if (loading) return <div><PageHeader eyebrow={t("cir.eyebrow")} title={t("cir.title")} /><Skeleton className="h-64 rounded-2xl" /></div>;

  return (
    <div>
      <Seo title={t("cir.title")} noindex />
      <PageHeader
        eyebrow={t("cir.eyebrow")}
        title={t("cir.title")}
        subtitle={t("cir.subtitle")}
        action={<Users className="h-6 w-6 text-primary" />}
      />

      <div className="mb-6 flex flex-wrap gap-3">
        {isCoach && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild><Button className="gap-1.5" data-testid="new-circle-btn"><Plus className="h-4 w-4" /> {t("cir.new_circle")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("cir.new_circle")}</DialogTitle>
                <DialogDescription>{t("cir.new_circle_desc")}</DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewKind("local")}
                  data-testid="new-kind-local"
                  className={`flex-1 rounded-xl border p-3 text-left text-sm transition-colors ${newKind === "local" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
                >
                  <span className="font-semibold text-foreground">{t("cir.local_circle")}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{t("cir.local_circle_desc")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewKind("individual")}
                  data-testid="new-kind-individual"
                  className={`flex-1 rounded-xl border p-3 text-left text-sm transition-colors ${newKind === "individual" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
                >
                  <span className="font-semibold text-foreground">{t("cir.mein_kreis")}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{t("cir.mein_kreis_desc")}</span>
                </button>
              </div>
              <Input placeholder={t("cir.name_ph")} value={newCircle.name} onChange={(e) => setNewCircle((c) => ({ ...c, name: e.target.value }))} data-testid="new-circle-name" />
              <Input placeholder={t("cir.desc_ph")} value={newCircle.description} onChange={(e) => setNewCircle((c) => ({ ...c, description: e.target.value }))} data-testid="new-circle-desc" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="new-cancel">{t("cir.cancel")}</Button>
                <Button onClick={create} disabled={busy || !newCircle.name.trim()} data-testid="new-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cir.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <div className="flex gap-2">
          <Input placeholder={t("cir.invite_code_ph")} value={joinCode} onChange={(e) => setJoinCode(e.target.value)} className="w-44" data-testid="circle-join-code" />
          <Button variant="outline" className="gap-1.5" onClick={join} disabled={busy || !joinCode.trim()} data-testid="circle-join"><LogIn className="h-4 w-4" /> {t("cir.join")}</Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2" data-testid="circle-list">
          {circles.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">{t("cir.empty")}</p>
          ) : circles.map((c) => {
            const h = HEALTH[c.health_signal?.level] || null;
            const kind = KIND[c.circle_kind] || KIND.local;
            const total = c.health_signal?.total_members ?? (c.members || []).length;
            const msgs7 = c.health_signal?.messages_7d ?? 0;
            const active7 = c.health_signal?.active_members_7d ?? 0;
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => open(c)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open(c)}
                data-testid={`open-circle-${c.id}`}
                className={`w-full cursor-pointer rounded-2xl border p-4 text-left transition-colors ${active?.id === c.id ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-accent"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 font-semibold text-foreground">{c.name}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${kind.cls}`} data-testid={`circle-kind-${c.id}`}>{kind.labelKey ? t(kind.labelKey) : kind.label}</span>
                </div>
                {c.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.description}</p>}
                {h && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground" data-testid={`health-signal-${c.id}`}>
                    <span className={`h-2 w-2 rounded-full ${h.dot}`} />
                    <span className="font-medium text-foreground">{t(h.labelKey)}</span>
                    <span>{t("cir.health_stat", { total, msgs: msgs7, active: active7 })}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); copyCode(c.invitation_code); }}
                  data-testid={`copy-code-${c.id}`}
                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:bg-accent"
                  title={t("cir.copy_invite_code")}
                >
                  <Copy className="h-3 w-3" /> {c.invitation_code}
                </button>
              </div>
            );
          })}
        </div>

        <div>
          {!active ? (
            <div className="flex h-full min-h-64 items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground">{t("cir.select_circle")}</div>
          ) : (
            <div className="rounded-2xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
                <div className="min-w-0">
                  <p className="font-display text-lg text-foreground">{active.name}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    {t("cir.invite_code_prefix")} {active.invitation_code}
                    <button className="ml-1 inline-flex hover:text-foreground" onClick={() => copyCode(active.invitation_code)} data-testid="circle-copy-code" title={t("cir.copy_code")}><Copy className="h-3 w-3" /></button>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setMembersOpen(true); loadMembers(active.id); }} data-testid="circle-open-members">
                    <Users className="h-3.5 w-3.5" /> {t("cir.members_count", { n: members.length || (active.health_signal?.total_members ?? 0) })}
                  </Button>
                  {isOwner && (
                    <Button variant="outline" size="icon" onClick={shareLink} data-testid="circle-share" title={t("cir.share_link")}><Link2 className="h-4 w-4" /></Button>
                  )}
                  {isOwner && (
                    <Button variant="outline" size="icon" onClick={() => navigate(`/app/coach/circles/c/${active.id}/members`)} data-testid="circle-open-health" title={t("cir.member_health")}><Activity className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>

              {active.welcome_message && (
                <div className="border-b border-border bg-primary/5 p-4" data-testid="circle-welcome-box">
                  <p className="text-sm text-foreground">{active.welcome_message}</p>
                </div>
              )}

              <div ref={scrollRef} className="max-h-80 space-y-2 overflow-y-auto p-4" data-testid="circle-messages">
                {messages.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="font-display text-base text-foreground">{t("cir.start_convo")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t("cir.start_convo_desc")}</p>
                  </div>
                ) : messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  const canPin = isOwner && !String(m.id).startsWith("local-");
                  return (
                    <div key={m.id} className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`circle-msg-${m.id}`}>
                      {mine && canPin && (
                        <button type="button" onClick={() => togglePin(m.id)} data-testid={`pin-${m.id}`} className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100" title={t("cir.pin")}><Pin className="h-3.5 w-3.5" /></button>
                      )}
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                        {!mine && <p className="mb-0.5 text-xs font-semibold opacity-80">{m.from_name}</p>}
                        <p>{m.body}</p>
                        <p className="mt-0.5 text-[10px] opacity-70">{shortDate(m.created_at)}</p>
                      </div>
                      {!mine && canPin && (
                        <button type="button" onClick={() => togglePin(m.id)} data-testid={`pin-${m.id}`} className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100" title={t("cir.pin")}><Pin className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 border-t border-border p-3">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("cir.msg_ph")} data-testid="circle-input" />
                <Button size="icon" onClick={send} disabled={!msg.trim()} data-testid="circle-send"><Send className="h-4 w-4" /></Button>
              </div>

              {isOwner && (
                <div className="flex items-center gap-2 border-t border-border p-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t("cir.invite_email_ph")} data-testid="circle-invite-email" />
                  <Button variant="outline" size="sm" onClick={invite} disabled={busy || !inviteEmail.trim()} data-testid="circle-invite-send">{t("cir.invite")}</Button>
                </div>
              )}

              <CircleExtras circleId={active.id} isOwner={isOwner} pinnedReload={pinnedReload} />
            </div>
          )}
        </div>
      </div>

      {/* Members management dialog */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("cir.manage_members")}</DialogTitle>
            <DialogDescription className="sr-only">{t("cir.manage_members_desc")}</DialogDescription>
          </DialogHeader>
          <p className="-mt-2 text-sm text-muted-foreground">{t("cir.members_count", { n: members.length })}</p>
          <div className="max-h-[60vh] space-y-1 overflow-y-auto" data-testid="circle-members-list">
            {members.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t("cir.no_members")}</p>
            ) : members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-xl border border-border/60 p-2.5" data-testid={`member-${m.id}`}>
                <Avatar className="h-9 w-9">
                  {m.profile_photo && <AvatarImage src={m.profile_photo} alt={m.name} />}
                  <AvatarFallback>{initials(m.name || m.email || "?")}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{m.name || m.email || t("cir.member_fallback")}</p>
                  {m.is_coach ? (
                    <Badge variant="secondary" className="mt-0.5 h-5 px-2 text-[10px]">{t("cir.coach")}</Badge>
                  ) : (m.email && <p className="truncate text-xs text-muted-foreground">{m.email}</p>)}
                </div>
                {isOwner && !m.is_coach && (
                  <Button variant="ghost" size="icon" onClick={() => setRemoveTarget(m)} data-testid={`remove-member-${m.id}`} title={t("cir.remove")}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          {isOwner && (
            <DialogFooter>
              <div className="flex w-full items-center gap-2">
                <UserPlus className="h-4 w-4 text-muted-foreground" />
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t("cir.invite_email_ph")} data-testid="members-invite-email" />
                <Button size="sm" onClick={invite} disabled={busy || !inviteEmail.trim()} data-testid="members-invite-send">{t("cir.invite")}</Button>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove-member confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cir.remove_member_q")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cir.remove_member_desc", { who: removeTarget?.name || removeTarget?.email || t("cir.this_member") })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">{t("cir.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} data-testid="remove-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t("cir.remove")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
